import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Modal for sharing a saved recipe — pick friends OR flash a QR code. */
export default function ShareRecipeModal({ recipeId, recipeTitle, onClose }) {
  const [tab, setTab] = useState("qr"); // qr | friends — QR first because it's the quick win
  const [friends, setFriends] = useState(null);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const qrRequestedRef = useRef(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    (async () => {
      try {
        const { data } = await api.get("/friends");
        setFriends(data.friends);
      } catch {
        setFriends([]);
      }
    })();
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Lazily fetch a share token + generate the QR the first time the QR tab is
  // viewed. We cache it for the life of the modal.
  useEffect(() => {
    if (tab !== "qr" || qrRequestedRef.current) return;
    qrRequestedRef.current = true;
    (async () => {
      setGenerating(true);
      try {
        const { data } = await api.post(`/recipes/${recipeId}/share`);
        const origin = (process.env.REACT_APP_SHORT_LINK_DOMAIN || window.location.origin).replace(/\/$/, "");
        const url = `${origin}/share/${data.share_token}`;
        setShareUrl(url);
        const png = await QRCode.toDataURL(url, {
          margin: 1,
          width: 520,
          color: { dark: "#2A332C", light: "#FFFFFFFF" },
          errorCorrectionLevel: "M",
        });
        setQrDataUrl(png);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't generate the QR");
      } finally {
        setGenerating(false);
      }
    })();
  }, [tab, recipeId]);

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));

  const send = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      toast.error("Pick at least one friend");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/recipes/${recipeId}/share-to-friends`, { friend_user_ids: ids });
      const delivered = data.delivered?.length || 0;
      const skipped = data.skipped?.length || 0;
      if (delivered) toast.success(`Shared with ${delivered} friend${delivered === 1 ? "" : "s"}`);
      if (skipped) toast.error(`${skipped} friend${skipped === 1 ? " is" : "s are"} not Pro yet`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const shareLink = async () => {
    if (!shareUrl) return;
    const text = `Try my ${recipeTitle} recipe on Shop, Cal & Meal 🍲 ${shareUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: recipeTitle, text }); return; } catch { /* cancelled */ }
    }
    await copyLink();
  };

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `${recipeTitle.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#2A332C]/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="share-recipe-modal"
    >
      <div className="ff-card w-full max-w-md p-6 max-h-[85vh] max-h-[85dvh] overflow-y-auto overscroll-contain relative my-auto" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="share-modal-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#F3F0EA] hover:bg-[#E5E2DA] flex items-center justify-center text-[#2A332C]"
        >
          ✕
        </button>
        <div className="ff-label">Share recipe</div>
        <h2 className="text-xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          {recipeTitle}
        </h2>

        <div className="flex gap-1 mt-4 rounded-full bg-[#F3F0EA] p-1" role="tablist">
          <TabBtn active={tab === "qr"} onClick={() => setTab("qr")} testid="share-tab-qr">📱 QR / link</TabBtn>
          <TabBtn active={tab === "friends"} onClick={() => setTab("friends")} testid="share-tab-friends">👥 To friends</TabBtn>
        </div>

        {tab === "qr" && (
          <div className="mt-5" data-testid="share-qr-panel">
            <p className="text-xs text-[#5C6B61]">
              Show this QR to anyone — they&apos;ll see the full recipe (ingredients, calories, method). No sign-up needed.
            </p>
            <div className="mt-4 rounded-2xl border border-[#E5E2DA] bg-white p-4 flex flex-col items-center">
              {generating || !qrDataUrl ? (
                <div className="w-56 h-56 bg-[#F3F0EA] rounded-xl flex items-center justify-center text-sm text-[#8B9690]">Generating QR…</div>
              ) : (
                <img src={qrDataUrl} alt={`QR code for ${recipeTitle}`} className="w-56 h-56" data-testid="share-qr-image" />
              )}
              {shareUrl && (
                <input
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                  className="ff-input w-full text-xs mt-3"
                  data-testid="share-qr-url"
                />
              )}
              <div className="mt-3 flex gap-2 w-full">
                <button onClick={shareLink} disabled={!shareUrl} className="flex-1 ff-btn-primary text-sm disabled:opacity-50" data-testid="share-qr-share-btn">Share link</button>
                <button onClick={copyLink} disabled={!shareUrl} className="rounded-full border border-[#E5E2DA] hover:border-[#3B5A3E] px-4 text-sm disabled:opacity-50" data-testid="share-qr-copy-btn">Copy</button>
                <button onClick={downloadQr} disabled={!qrDataUrl} className="rounded-full border border-[#E5E2DA] hover:border-[#3B5A3E] px-4 text-sm disabled:opacity-50" data-testid="share-qr-download-btn" title="Download PNG">⬇</button>
              </div>
            </div>
          </div>
        )}

        {tab === "friends" && (
          <div className="mt-5">
            <p className="text-xs text-[#8B9690]">A copy will appear in their Recipes list.</p>
            {friends === null && <div className="text-[#5C6B61] mt-4">Loading friends…</div>}
            {friends && friends.length === 0 && (
              <div className="mt-5 text-center" data-testid="no-friends-state">
                <p className="text-sm text-[#5C6B61]">You don&apos;t have any friends yet.</p>
                <Link to="/app/friends" onClick={onClose} className="ff-btn-primary text-sm inline-block mt-3" data-testid="invite-friend-link">
                  Invite a friend
                </Link>
              </div>
            )}
            {friends && friends.length > 0 && (
              <>
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto" data-testid="share-friend-list">
                  {friends.map((f) => {
                    const initials = (f.name || f.email || "?").slice(0, 1).toUpperCase();
                    const checked = !!selected[f.user_id];
                    return (
                      <label
                        key={f.user_id}
                        className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition ${checked ? "bg-[#E8F0E9]" : "hover:bg-[#F3F0EA]"}`}
                        data-testid={`share-friend-${f.user_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(f.user_id)}
                          className="accent-[#3B5A3E] w-4 h-4"
                        />
                        {f.picture ? (
                          <img src={f.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-sm font-semibold">{initials}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#2A332C] truncate">{f.name || f.email}</div>
                          <div className="text-[11px] text-[#8B9690] truncate">{f.email}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <button
                  onClick={send}
                  disabled={sending}
                  data-testid="share-send-btn"
                  className="w-full mt-5 ff-btn-primary text-sm disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send recipe"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 rounded-full text-sm py-1.5 font-medium transition ${active ? "bg-white text-[#2A332C] shadow-sm" : "text-[#5C6B61] hover:text-[#2A332C]"}`}
    >
      {children}
    </button>
  );
}
